import { ReactNode } from 'react';

import { Column } from 'react-table';

export interface NotificationBannerProps {
    icon?: ReactNode;
    title?: string;
    message?: string | ReactNode;
    variant?: 'info' | 'warning' | 'danger' | 'success';
    customStyles?: React.CSSProperties;
}

export type FieldType = 'WEBSITE_LINK' | 'BUSINESS_HOURS' | 'PHONE_NUMBER' | 'DESCRIPTION' | 'UNKNOWN';

export interface FieldTypeConfig {
    label: string;
    icon: string;
    type: 'single' | 'nested';
}

export interface LocationDataRow {
    id: string;
    locationUid: number;
    locationName: string;
    googleData: string | any[] | null;
    reputationData: string | any[] | null;
    fieldType: 'single' | 'nested';
    field?: FieldType;
    fieldIcon?: string;
    status: 'missing' | 'mismatch' | 'success' | 'error';
    errorMessage?: string;
}

export interface TableConfig<T extends object = LocationDataRow> {
    showCheckboxes?: boolean;
    pageSize?: number;
    columns?: Column<T>[];
    enableZebraStriping?: boolean;
    enableHover?: boolean;
    isCompletionView?: boolean;
}

export interface PaginationInfoProps {
    currentPage: number;
    pageSize: number;
    totalItems: number;
}

export interface ColumnHeaderProps {
    icon?: ReactNode;
    iconColor?: string;
    title: string;
    subtitle?: string;
    subtitleIcon?: string;
}

export type ModalState = 'idle' | 'loading' | 'no-data' | 'ready' | 'updating' | 'completed' | 'summary';

export type CompletionStatus = 'full-success' | 'partial-success' | 'full-error';

export type SummaryStatus = 'full-success' | 'partial-success' | 'full-error';

export interface MissingProfileDataModalProps {
    field?: FieldType;
    modalTitle?: string;
    modalSize?: 'sm' | 'lg' | 'xl';
    notificationConfig?: NotificationBannerProps;
    tableData?: LocationDataRow[];
    fetchData?: () => Promise<{
        tableData?: LocationDataRow[];
    }>;
    children?: ReactNode;
    renderContent?: (context: {
        modalData: {
            tableData?: LocationDataRow[];
        };
        selectedRows: LocationDataRow[];
        handleRowSelect: (rows: LocationDataRow[]) => void;
        handleAction: (actionType: string) => void;
        isLoading: boolean;
        isUpdating: boolean;
        isCompleted: boolean;
        progress: number;
        progressStats: {
            totalProfiles: number;
            profilesUpdated: number;
            profilesWithErrors: number;
        };
        completionScenario: CompletionStatus;
    }) => ReactNode;
    completionScenario?: CompletionStatus;
    baseURL?: string;
    importJobId?: string;
    notificationId?: string;
}

export interface MissingProfileDataModalRef {
    openModal: () => void;
    openCompletionModal: (scenario?: CompletionStatus) => void;
    closeModal: () => void;
}

export interface ProgressStats {
    totalProfiles: number;
    profilesUpdated: number;
    profilesWithErrors: number;
}

export interface SummaryData {
    status: SummaryStatus;
    syncedCount: number;
    totalCount: number;
    timeTaken: string;
}

export interface NestedDataItem {
    day?: string;
    hours?: string;
    type?: string;
    [key: string]: any;
}

export interface SingleDataCellProps {
    data: any;
    isMissing?: boolean;
    icon?: string;
}

export interface NestedDataCellProps {
    data: NestedDataItem[] | string[];
    icon?: string;
    labelKey?: string;
    type?: string;
}

export interface LocationCellProps {
    locationName: string;
}

export interface ProgressStatsProps {
    totalProfiles: number;
    profilesUpdated: number;
    profilesWithErrors: number;
    isCompleted?: boolean;
}

export interface CancelConfirmationModalProps {
    show: boolean;
    onDoNotCancel: () => void;
    onConfirmCancel: () => void;
    disabled?: boolean;
}

export interface CompletionBannerProps {
    scenario: CompletionStatus;
    profilesUpdated: number;
    profilesWithErrors: number;
    totalProfiles: number;
    field?: FieldType;
    onDownloadErrors?: () => void;
}

export interface FinalSummaryContentProps {
    status: SummaryStatus;
    syncedCount: number;
    totalCount: number;
    timeTaken: string;
    onDownloadErrors?: () => void;
}

export interface IndeterminateCheckboxProps {
    indeterminate?: boolean;
    [key: string]: any;
}

export interface ModalStructureProps {
    show: boolean;
    onHide: () => void;
    title?: string;
    size?: 'sm' | 'lg' | 'xl';
    children: ReactNode;
    footer?: ReactNode;
    closeButton?: boolean;
    closeOnBackdropClick?: boolean;
    closeOnEscape?: boolean;
    dialogClassName?: string;
    contentClassName?: string;
    bodyClassName?: string;
}

export interface NotificationModalContentProps {
    notificationConfig?: NotificationBannerProps;
    tableData?: LocationDataRow[];
    tableConfig?: TableConfig;
    onRowSelect?: (selectedRows: LocationDataRow[]) => void;
    showNotification?: boolean;
    showTable?: boolean;
    className?: string;
    isUpdating?: boolean;
    isCompleted?: boolean;
    progress?: number;
    progressStats?: {
        totalProfiles: number;
        profilesUpdated: number;
        profilesWithErrors: number;
    };
    completionScenario?: CompletionStatus;
    field?: FieldType;
    onDownloadErrors?: () => void;
}

export interface DataTableProps {
    data: LocationDataRow[];
    config?: TableConfig;
    onRowSelect?: (selectedRows: LocationDataRow[]) => void;
}

export interface CompletionStats {
    totalProfiles: number;
    successCount: number;
    failureCount: number;
}

// API Types - Notifications
export interface NotificationSchedule {
    subject: string;
    body: string | null;
    frequency: number;
    active: boolean;
    sentDate: number;
    nextDate: number;
    distributions: any[];
    format: number;
    daysOfWeek: number[] | null;
    daysOfMonth: number[] | null;
}

export interface NotificationDetail {
    id: string;
    created: number;
    updated: number;
    agencyID: number;
    tenantID: number;
    userID: number;
    type: number;
    channels: number[];
    externalID: string | null;
    filters: Array<{
        name: string;
        values: string[];
    }>;
    parameters: {
        fields?: string[];
        [key: string]: any;
    };
    activeNotification: boolean;
    name: string;
    lambdaID: string | null;
    notificationSchedule: NotificationSchedule;
    roleID: string | null;
    parentNotificationID: string | null;
    customMessage: string | null;
    cID: number;
    uID: number;
    batch: boolean;
    hide: boolean;
    syncStatus: any;
    originalSourceID: string | null;
}

export interface GetNotificationResponse {
    response: {
        responseCode: number;
        errors: any;
        message: string | null;
    };
    notification: NotificationDetail;
}

// API Types - BL Audits
export interface AuditEntity {
    locationUid: number;
    id: string;
    auditMatchData: any;
}

export interface MissingFieldData {
    locationUid: number;
    sourceID: string;
    field: string;
    collected: any[];
}

// API Types - Import Jobs
export enum ImportJobStatus {
    Pending = 1,
    Cancelled = 2,
    Running = 3,
    Completed = 4,
}

export enum PatchOperation {
    Add = 1,
    Update = 2,
    Delete = 3,
    Upsert = 4,
}

export enum ImportType {
    Profile = 1,
    Location = 2,
    Request = 3,
    Survey = 4,
    User = 5,
    Comment = 6,
    Customer = 7,
    ProfileInsurance = 8,
}

export enum Priority {
    Immediate = 1,
    High = 2,
    Low = 3,
    Normal = 4,
}

export enum FileType {
    CSV = 0,
    JSON = 1,
    XML = 2,
}

export enum ExportMode {
    ErrorsOnly = 0,
    WarningsAndErrors = 1,
    All = 2,
    SuccessOnly = 3,
}

export interface DiffMetric {
    total: number;
    additions: number;
    deletions: number;
    updates: number;
    unchanged: number;
    errors: number;
    failed: number;
    osp: number;
    unsubscribed: number;
}

export interface ImportJobMetric {
    diffMetric: DiffMetric;
}

export interface ImportJobPhase {
    importJobStatus?: ImportJobStatus;
    importJobMetric: ImportJobMetric;
    startMillis?: number;
    endMillis?: number;
    durationMillis?: number;
}

export interface User {
    id: string;
    tenantID: number;
    displayName: string;
}

export interface ImportJob {
    id: string;
    importJobID: string;
    tenantID: number;
    agencyID: number;
    importJobStatus: ImportJobStatus;
    importType?: ImportType;
    validationPhase?: ImportJobPhase;
    commitPhase?: ImportJobPhase;
    startMillis?: number;
    endMillis?: number;
    durationMillis?: number;
    createdByID?: string;
    committedByID?: string;
    createdBy?: User;
    committedBy?: User;
    importFile?: string;
    importFileName?: string;
    importMappingID?: string;
    headerMappings?: any;
}

export interface PatchMap {
    [key: string]: any;
}

export interface Patch {
    patchMap: PatchMap;
    patchOperation: PatchOperation;
}

export interface BulkImportMultiPatchRequest {
    patches: Patch[];
    importType: ImportType;
    skipValidation: boolean;
    priority: Priority;
    parameters?: {
        notificationHistoryID?: string;
    };
}

export interface JobAndImportJobID {
    job: {
        id: string;
        routingKey: string;
        jobState: string;
        request?: any;
        response?: string;
        batch?: boolean;
        failures?: number;
    };
    importJobID: string;
}

export interface ImportJobRowsErrorsExportRequest {
    showCSVHeaders?: boolean;
    searchRequest: {
        filters: Array<{
            name: string;
            values: string[];
        }>;
        start?: number;
    };
    options?: {
        fileType?: FileType;
        mode?: ExportMode;
        cutoffTimeSec?: number;
    };
}

export interface ImportJobRowsErrorsExportResponse {
    resource: string;
}

export interface ValidationError {
    message?: string;
}

export interface ImportJobRow {
    id: string;
    importJobID: string;
    tenantID: number;
    agencyID: number;
    rowNumber: number;
    rowData?: any;
    values?: { [key: string]: any };
    headerValues?: { [key: string]: any };
    validationErrors?: ValidationError[];
    diffData?: any;
    rowStatus?: number;
    created?: number;
    updated?: number;
}

export interface ImportJobRowsSearchRequest {
    filters: Array<{
        name: string;
        values: string[];
        operator?: number;
        logical?: number;
    }>;
    projections?: string[];
    sorts?: Array<{
        name: string;
        sortOrder: number;
    }>;
    count?: number;
    start?: number;
}

export interface ImportJobRowsArgs {
    includeDiffs?: boolean;
    importType?: ImportType;
    includeDetailDiff?: boolean;
}

// Hook Types - useImportJob
export interface ImportJobState {
    status: 'idle' | 'creating' | 'running' | 'completed' | 'error' | 'cancelled';
    progress: number;
    totalRows: number;
    processedRows: number;
    successRows: number;
    errorRows: number;
    unchangedRows: number;
    startTime: number | null;
    elapsedTime: number;
    importJobId: string | null;
    jobId: string | null;
    error: Error | null;
}

export interface UseImportJobResult {
    state: ImportJobState;
    startImportJob: (rows: LocationDataRow[], notificationHistoryId?: string) => Promise<void>;
    cancelJob: () => Promise<void>;
    downloadErrors: (importJobId?: string) => Promise<void>;
    batchJob: () => Promise<void>;
    reset: () => void;
}

// Hook Types - useModalData
export interface AlertMetadata {
    subtext?: string;
    field: FieldType;
    importJobID?: string;
}

export interface DesktopAlert {
    id: string;
    agencyID: number;
    tenantID: number;
    userID: number;
    objectIDs: string[];
    notificationHistoryIDs: string[];
    eventType: string;
    createdDate: number;
    metadata: AlertMetadata;
}

export interface UseModalDataParams {
    agencyId?: number;
    tenantId?: number;
    notificationId?: string; // Keep for backward compatibility
    fieldName?: FieldType;
    objectIds?: string[];
}

export interface UseModalDataResult {
    loading: boolean;
    error: Error | null;
    tableData: LocationDataRow[];
    field: FieldType;
    modalTitle: string;
    modalTitleKey: string;
    locationCount: number;
    fetchData: () => Promise<void>;
}

// Hook Types - useCompletionData
export interface UseCompletionDataParams {
    agencyId?: number;
    tenantId?: number;
    notificationId?: string; // Keep for backward compatibility
    importJobId?: string;
    apiField?: FieldType;
}

export interface UseCompletionDataResult {
    loading: boolean;
    error: Error | null;
    completionData: LocationDataRow[];
    stats: {
        totalProfiles: number;
        profilesUpdated: number;
        profilesWithErrors: number;
    };
    field: FieldType;
    totalDurationMillis: number;
    fetchCompletionData: () => Promise<void>;
}
